import { ReactNode } from 'react';
import { Box } from '@mui/material';
import DisabledByDefaultOutlinedIcon from '@mui/icons-material/DisabledByDefaultOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { SvgIconComponent } from '@mui/icons-material';
import colors from '../../../../assets/theme/base/colors';
import { DeviationsDto } from '../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import { SeverityLevel } from '../../../../Component/Model/Enum/EmploymentEquity';
import { SeverityChipCounts, formatPercentage } from './justificationsHelper';

interface ChipPalette {
    border: string;
    bg: string;
    text: string;
}

interface HeaderChipProps {
    palette: ChipPalette;
    Icon?: SvgIconComponent;
    width?: string;
    children: ReactNode;
}

function HeaderChip({ palette, Icon, width, children }: HeaderChipProps) {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                width,
                px: width ? 0 : '12px',
                py: '4px',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 600,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                border: `1px solid ${palette.border}`,
                backgroundColor: palette.bg,
                color: palette.text,
            }}
        >
            {Icon && <Icon sx={{ width: 14, height: 14, flexShrink: 0 }} />}
            {children}
        </Box>
    );
}

interface SeverityChipConfig {
    countKey: keyof SeverityChipCounts;
    label: string;
    Icon: SvgIconComponent;
    palette: ChipPalette;
}

const severityChipConfig: SeverityChipConfig[] = [
    { countKey: 'critical', label: 'critical', Icon: DisabledByDefaultOutlinedIcon, palette: colors.accordionHeaderChip.critical },
    { countKey: 'high',     label: 'high',     Icon: WarningAmberOutlinedIcon,      palette: colors.accordionHeaderChip.high },
    { countKey: 'medium',   label: 'medium',   Icon: InfoOutlinedIcon,              palette: colors.accordionHeaderChip.medium },
    { countKey: 'onTrack',  label: 'on track', Icon: CheckCircleOutlinedIcon,       palette: colors.accordionHeaderChip.onTrack },
];

interface SeverityCountChipsProps {
    counts: SeverityChipCounts;
}

export function SeverityCountChips({ counts }: SeverityCountChipsProps) {
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {severityChipConfig.map(({ countKey, label, Icon, palette }) => (
                <HeaderChip key={countKey} palette={palette} Icon={Icon} width="96px">
                    {counts[countKey]}
                    <Box component="span" sx={{ opacity: 0.8 }}>{label}</Box>
                </HeaderChip>
            ))}
        </Box>
    );
}

interface DesignatedDisabledStatusChipProps {
    designatedDisabled: DeviationsDto.HeatmapDesignatedDisabledDto;
}

export function DesignatedDisabledStatusChip({ designatedDisabled }: DesignatedDisabledStatusChipProps) {
    const hasDeviation: boolean =
        designatedDisabled.severity === SeverityLevel.Critical ||
        designatedDisabled.severity === SeverityLevel.High ||
        designatedDisabled.severity === SeverityLevel.Medium;
    const palette: ChipPalette = hasDeviation ? colors.accordionHeaderChip.critical : colors.accordionHeaderChip.onTrack;
    const Icon: SvgIconComponent = hasDeviation ? DisabledByDefaultOutlinedIcon : CheckCircleOutlinedIcon;
    const statusLabel: string = hasDeviation ? 'Deviation' : 'On track';

    return (
        <HeaderChip palette={palette} Icon={Icon}>
            {statusLabel} - {formatPercentage(designatedDisabled.currentPercentage)} vs {formatPercentage(designatedDisabled.targetPercentage)} target
        </HeaderChip>
    );
}

interface KeyReasonChipProps {
    captured: boolean;
}

export function KeyReasonChip({ captured }: KeyReasonChipProps) {
    const palette: ChipPalette = captured ? colors.accordionHeaderChip.captured : colors.accordionHeaderChip.neutral;
    const label: string = captured ? 'EEA2 key justifiable reason captured' : 'EEA2 key justifiable reason not captured';

    return (
        <HeaderChip palette={palette} width="288px">
            {label}
        </HeaderChip>
    );
}
